/**
 * Deal Analyzer — investment sandbox calculator.
 *
 * Left:  editor with collapsible sections (Property / Financing / Income / Opex)
 * Right: live results panel with the 4 headline metrics + breakdown + warnings
 *
 * The math runs entirely client-side via `lib/dealMath` for instant feedback.
 * The backend only persists raw inputs and handles the "Convert to Asset" jump.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calculator, Plus, Trash2, Copy, ArrowRight, Save, Bed, CalendarRange,
  Building2, Banknote, TrendingUp, AlertTriangle, CheckCircle2, FileText,
  ChevronDown, ChevronRight as ChevronRightIcon, Briefcase, Sparkles,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { computeDeal, validateDeal } from "../lib/dealMath";
import { toast } from "../components/ui/sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import PageIntro from "../components/PageIntro";
import ConfirmDialog from "../components/ConfirmDialog";
import { HelpCircle } from "lucide-react";

const CURRENCY_SYMBOLS = { EUR: "€", AED: "AED", GBP: "£", TRY: "₺", USD: "$" };

/**
 * Grunderwerbsteuer per Bundesland (Stand 2026 — aktuelle Sätze).
 * Source: Finanzministerien der Länder.
 */
const DE_BUNDESLAENDER = [
  { code: "bayern",                label: "Bayern",                  grest: 3.5 },
  { code: "sachsen",               label: "Sachsen",                 grest: 3.5 },
  { code: "hamburg",               label: "Hamburg",                 grest: 5.5 },
  { code: "bremen",                label: "Bremen",                  grest: 5.0 },
  { code: "niedersachsen",         label: "Niedersachsen",           grest: 5.0 },
  { code: "rheinland-pfalz",       label: "Rheinland-Pfalz",         grest: 5.0 },
  { code: "sachsen-anhalt",        label: "Sachsen-Anhalt",          grest: 5.0 },
  { code: "baden-wuerttemberg",    label: "Baden-Württemberg",       grest: 5.0 },
  { code: "mecklenburg-vorpommern",label: "Mecklenburg-Vorpommern",  grest: 6.0 },
  { code: "berlin",                label: "Berlin",                  grest: 6.0 },
  { code: "hessen",                label: "Hessen",                  grest: 6.0 },
  { code: "brandenburg",           label: "Brandenburg",             grest: 6.5 },
  { code: "nrw",                   label: "Nordrhein-Westfalen",     grest: 6.5 },
  { code: "saarland",              label: "Saarland",                grest: 6.5 },
  { code: "schleswig-holstein",    label: "Schleswig-Holstein",      grest: 6.5 },
  { code: "thueringen",            label: "Thüringen",               grest: 5.0 },
];
const DE_NOTAR_DEFAULT_PCT  = 1.6;   // Notar + Grundbuch
const DE_MAKLER_DEFAULT_PCT = 3.57;  // hälftig geteilt seit BestellerprinzipNovellierung (Käuferanteil)

// Sensible defaults seeded into a brand-new draft.
const DEFAULT_DEAL = {
  name: "New Deal",
  location: "dubai",
  currency: "AED",
  purchase_price: 0,
  closing_costs: 0,
  renovation_costs: 0,
  use_financing: false,
  loan_amount: 0,
  interest_rate_pct: 4.5,
  tilgungsrate_pct: 2.5,
  amortization: "amortizing",
  income_mode: "long_term",
  monthly_rent: 0,
  nightly_rate: 0,
  occupancy_pct: 65,
  service_charges_annual: 0,
  insurance_annual: 0,
  management_fee_annual: 0,
  maintenance_reserve_annual: 0,
  vacancy_reserve_pct: 2,
  utilities_annual: 0,
  cleaning_monthly: 0,
  notes: "",
  // Germany-only running costs split (per Betriebskostenverordnung):
  hausgeld_umlage_monthly: 0,           // umlagefähig — recoverable from tenant (informational only)
  hausgeld_nicht_umlage_monthly: 0,     // nicht-umlagefähig — landlord cost
  instandhaltungsruecklage_monthly: 0,  // maintenance reserve — landlord cost
  // Germany-only line-item breakdown of closing costs (alle editierbar):
  de_bundesland: "bayern",
  de_grest_pct: 3.5,
  de_notar_pct: DE_NOTAR_DEFAULT_PCT,
  de_makler_pct: DE_MAKLER_DEFAULT_PCT,
  // North Cyprus-only fields (Feb 2026):
  is_new_build: true,            // controls VAT 5% + Transformer fee
  transformer_fee: 2000,         // GBP, only new builds
  solicitor_fee: 2500,           // GBP fixed
  property_tax_annual: 0,        // GBP/year
  // Cyprus statutory fee overrides — empty = auto-compute, any number = manual
  stamp_duty_override: "",
  vat_override: "",
  transfer_fee_override: "",
  // Dubai-only Excel-parity fields (Feb 2026):
  furniture_cost: 0,           // Möblierung
  cheque_fee: 0,               // Scheckerstellungsgebühr (5,000 AED if no UAE bank)
  financing_setup_costs: 0,    // Sonstige Finanzierungskosten (one-time)
  wire_transfer_fee: "",       // Manual AED amount (no auto-calc)
  // Per-line manual overrides for Dubai breakdown (click amount to edit):
  dld_fee_override: "",        // empty → auto 4%
  broker_override: "",         // empty → auto 2.1%
  trustee_override: "",        // empty → auto tiered 2,100 / 4,200
};

// Locations where users intuitively think in monthly recurring costs
// (Hausgeld in DE, monthly common-charge billing in CY) vs. annual
// (Dubai service charges are billed quarterly but quoted AED/sqft/year).
const MONTHLY_OPEX_LOCATIONS = new Set(["germany", "north_cyprus"]);

// Migrate legacy drafts (pre Feb 2026) that stored `equity` but not
// `loan_amount`. Computes loan_amount = TPC − equity so the new UI input
// pre-fills correctly without forcing users to re-enter the loan.
function migrateLegacyDraft(d) {
  if (!d) return d;
  const hasLoan = d.loan_amount !== undefined && d.loan_amount !== null && d.loan_amount !== "" && Number(d.loan_amount) > 0;
  if (hasLoan) return d;
  if (!d.use_financing) return d;
  const tpc = (Number(d.purchase_price) || 0) + (Number(d.closing_costs) || 0) + (Number(d.renovation_costs) || 0);
  const equity = Number(d.equity) || 0;
  const loan = Math.max(tpc - equity, 0);
  if (loan <= 0) return d;
  return { ...d, loan_amount: loan };
}

const fmt = (n, digits = 0) =>
  Number.isFinite(n)
    ? new Intl.NumberFormat("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n)
    : "—";

const fmtPct = (n, digits = 2) => (Number.isFinite(n) ? `${n.toFixed(digits)}%` : "—");

export default function DealAnalyzerPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [draft, setDraft] = useState(DEFAULT_DEAL);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmConvert, setConfirmConvert] = useState(false);
  const [showConverted, setShowConverted] = useState(false);

  // Load deals on mount
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/deals");
        setDeals(data);
        const active = data.filter((d) => !d.converted_to_asset_id)[0];
        if (active) {
          setActiveId(active.id);
          setDraft(migrateLegacyDraft(active));
        }
      } catch (e) {
        toast.error("Could not load deals");
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  // ----- Live math ---------------------------------------------------------
  const result = useMemo(() => computeDeal(draft), [draft]);
  const warnings = useMemo(() => validateDeal(draft, result), [draft, result]);

  const sym = CURRENCY_SYMBOLS[draft.currency] || draft.currency;
  const isStr = draft.income_mode === "short_term";
  const isGermany = draft.location === "germany";

  const visibleDeals = deals.filter((d) =>
    showConverted ? true : !d.converted_to_asset_id,
  );

  // ----- Handlers ---------------------------------------------------------
  // Default currency per jurisdiction. When the user switches location we
  // auto-flip the currency to the locally-traded one (AED/GBP/EUR). They
  // can still manually override afterwards — the auto-flip only fires on
  // the location change itself.
  const DEFAULT_CURRENCY_BY_LOCATION = {
    dubai: "AED",
    north_cyprus: "GBP",
    germany: "EUR",
  };

  const set = (key, value) => {
    setDraft((d) => {
      if (key === "location" && value !== d.location) {
        const next = DEFAULT_CURRENCY_BY_LOCATION[value];
        if (next && next !== d.currency) {
          return { ...d, location: value, currency: next };
        }
      }
      return { ...d, [key]: value };
    });
  };

  const handleNewDeal = async () => {
    const tmpName = `Deal ${deals.length + 1}`;
    try {
      const { data } = await api.post("/deals", { ...DEFAULT_DEAL, name: tmpName });
      setDeals([data, ...deals]);
      setActiveId(data.id);
      setDraft(migrateLegacyDraft(data));
      toast.success("New deal created");
    } catch (e) {
      toast.error("Could not create deal");
    }
  };

  const handleSelect = (deal) => {
    setActiveId(deal.id);
    setDraft(migrateLegacyDraft(deal));
  };

  const handleSave = async () => {
    if (!activeId) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/deals/${activeId}`, draft);
      setDeals(deals.map((d) => (d.id === activeId ? data : d)));
      setDraft(migrateLegacyDraft(data));
      toast.success("Saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async () => {
    try {
      const { data } = await api.post("/deals", {
        ...draft,
        name: `${draft.name} (copy)`,
      });
      setDeals([data, ...deals]);
      setActiveId(data.id);
      setDraft(migrateLegacyDraft(data));
      toast.success("Duplicated");
    } catch (e) {
      toast.error("Could not duplicate");
    }
  };

  const handleDelete = async () => {
    if (!activeId) return;
    try {
      await api.delete(`/deals/${activeId}`);
      const remaining = deals.filter((d) => d.id !== activeId);
      setDeals(remaining);
      const next = remaining.filter((d) => !d.converted_to_asset_id)[0];
      if (next) {
        setActiveId(next.id);
        setDraft(migrateLegacyDraft(next));
      } else {
        setActiveId(null);
        setDraft(DEFAULT_DEAL);
      }
      setConfirmDelete(false);
      toast.success("Deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const handleConvert = async () => {
    if (!activeId) return;
    setConverting(true);
    try {
      await api.put(`/deals/${activeId}`, draft); // ensure latest state is saved
      const { data } = await api.post(`/deals/${activeId}/convert-to-asset`);
      toast.success("Asset added to your portfolio");
      setConfirmConvert(false);
      navigate("/portfolio");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Conversion failed";
      toast.error(msg);
    } finally {
      setConverting(false);
    }
  };

  const isConverted = !!draft.converted_to_asset_id;

  // ===========================================================================
  return (
    <TooltipProvider delayDuration={120}>
    <div data-testid="deal-analyzer-page">
      <PageIntro
        pageKey="deals"
        title="Deal Analyzer"
        subtitle="A calculator for properties you're considering — before you wire any money."
        bullets={[
          { icon: Calculator, label: "Live calculator", text: "Every change updates Monthly Cashflow, Gross & Net ROI, Cash-on-Cash and DSCR instantly." },
          { icon: Bed, label: "Long-term or short-term", text: "Toggle to model an Airbnb vs. a year-long lease — the engine adjusts opex (cleaning, occupancy, utilities) automatically." },
          { icon: ArrowRight, label: "Convert to Asset", text: "When you actually buy the property, one click migrates the deal into your real portfolio with all fields preserved." },
        ]}
      />

      {/* Header */}
      <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37] mb-2">Sandbox</p>
          <h1 className="font-serif text-3xl md:text-4xl text-white tracking-tight">Deal Analyzer</h1>
          <p className="text-white/60 text-sm mt-2 max-w-2xl">
            Model any potential investment. Change a number, see the cashflow.
            When you're ready to buy, convert it to a tracked asset.
          </p>
        </div>
        <button
          onClick={handleNewDeal}
          data-testid="new-deal-btn"
          className="bg-[#D4AF37] hover:bg-[#E8C757] text-black text-xs font-semibold tracking-[0.15em] uppercase px-5 py-2.5 inline-flex items-center gap-2 transition-colors"
        >
          <Plus className="w-4 h-4" /> New Deal
        </button>
      </div>

      {loading ? (
        <div className="text-white/60 text-sm py-12 text-center">Loading deals…</div>
      ) : visibleDeals.length === 0 && !activeId ? (
        <EmptyState onCreate={handleNewDeal} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
          {/* Sidebar: deal list */}
          <DealsSidebar
            deals={visibleDeals}
            activeId={activeId}
            onSelect={handleSelect}
            showConverted={showConverted}
            onToggleConverted={() => setShowConverted((s) => !s)}
            hasConverted={deals.some((d) => d.converted_to_asset_id)}
          />

          {/* Main: editor + results */}
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_400px] gap-6">
            {/* Editor */}
            <div className="space-y-3">
              {/* Toolbar */}
              <div className="bg-white/[0.02] border border-white/[0.10] px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                <input
                  data-testid="deal-name-input"
                  value={draft.name}
                  onChange={(e) => set("name", e.target.value)}
                  className="bg-transparent text-white text-base font-serif flex-1 min-w-[180px] outline-none focus:bg-white/[0.04] px-2 py-1 -mx-2"
                  placeholder="Deal name"
                  disabled={isConverted}
                />
                <div className="flex items-center gap-1.5">
                  <ToolbarBtn icon={Save} label="Save" onClick={handleSave} loading={saving} disabled={isConverted} testId="save-deal-btn" />
                  <ToolbarBtn icon={Copy} label="Duplicate" onClick={handleDuplicate} testId="duplicate-deal-btn" />
                  <ToolbarBtn icon={Trash2} label="Delete" onClick={() => setConfirmDelete(true)} testId="delete-deal-btn" danger />
                </div>
              </div>

              {isConverted && (
                <div className="bg-emerald-500/5 border border-emerald-500/20 px-4 py-3 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <p className="text-emerald-200/80 text-xs">
                    Converted to asset on {(draft.converted_at || "").slice(0, 10)}. This deal is now read-only.
                  </p>
                </div>
              )}

              {/* Section: Property */}
              <Section title="Property" icon={Building2} defaultOpen>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Location">
                    <select
                      data-testid="deal-location"
                      value={draft.location}
                      onChange={(e) => set("location", e.target.value)}
                      disabled={isConverted}
                      className={inputCls}
                    >
                      <option value="dubai">Dubai (UAE)</option>
                      <option value="north_cyprus">North Cyprus</option>
                      <option value="germany">Germany</option>
                    </select>
                  </Field>
                  <Field label="Currency">
                    <select
                      data-testid="deal-currency"
                      value={draft.currency}
                      onChange={(e) => set("currency", e.target.value)}
                      disabled={isConverted}
                      className={inputCls}
                    >
                      <option value="EUR">EUR (€)</option>
                      <option value="AED">AED</option>
                      <option value="GBP">GBP (£)</option>
                      <option value="TRY">TRY (₺)</option>
                      <option value="USD">USD ($)</option>
                    </select>
                  </Field>
                  <MoneyField testId="purchase-price" sym={sym} label="Purchase price" value={draft.purchase_price} onChange={(v) => set("purchase_price", v)} disabled={isConverted} />
                  {draft.location === "germany" ? (
                    <GermanClosingCosts draft={draft} set={set} disabled={isConverted} />
                  ) : draft.location === "dubai" ? (
                    <DubaiClosingCosts draft={draft} set={set} sym={sym} disabled={isConverted} />
                  ) : draft.location === "north_cyprus" ? (
                    <CyprusClosingCosts draft={draft} set={set} sym={sym} disabled={isConverted} />
                  ) : (
                    <MoneyField testId="closing-costs" sym={sym} label="Closing costs (taxes, notary, agent)" value={draft.closing_costs} onChange={(v) => set("closing_costs", v)} disabled={isConverted} />
                  )}
                  <MoneyField testId="renovation-costs" sym={sym} label="Renovation costs" value={draft.renovation_costs} onChange={(v) => set("renovation_costs", v)} disabled={isConverted} />
                </div>
              </Section>

              {/* Section: Financing */}
              <Section title="Financing" icon={Banknote}>
                <label className="flex items-center gap-3 cursor-pointer mb-3">
                  <input
                    type="checkbox"
                    data-testid="use-financing"
                    checked={draft.use_financing}
                    onChange={(e) => set("use_financing", e.target.checked)}
                    disabled={isConverted}
                    className="w-4 h-4 accent-[#D4AF37]"
                  />
                  <span className="text-white/80 text-sm">Mortgage / loan financing</span>
                </label>

                {draft.use_financing && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <MoneyField
                      testId="loan-amount"
                      sym={sym}
                      label="Loan amount"
                      tip="The bank loan amount — what the bank approved (Darlehenssumme on your Darlehensvertrag). Your equity contribution is the residual: Total Project Cost − Loan. The loan is capped at 100% LTV (no negative equity)."
                      value={draft.loan_amount}
                      onChange={(v) => set("loan_amount", v)}
                      disabled={isConverted}
                    />
                    <NumberField testId="interest-rate" suffix="%" label="Interest rate (annual)" value={draft.interest_rate_pct} onChange={(v) => set("interest_rate_pct", v)} step={0.05} disabled={isConverted} />
                    <NumberField
                      testId="tilgungsrate"
                      suffix="% p.a."
                      label="Amortization rate"
                      tip="Annual principal repayment as % of the original loan (Tilgung / Anfangstilgung in German banking). In an amortizing loan the total monthly payment stays constant; the principal/interest mix shifts as the balance shrinks. Typical: 2–4% p.a. → ≈ 25–35 year payoff."
                      value={draft.tilgungsrate_pct}
                      onChange={(v) => set("tilgungsrate_pct", v)}
                      step={0.1}
                      disabled={isConverted}
                    />
                    <Field
                      label="Amortization type"
                      tip="Amortizing (default): you pay interest + principal every month at a constant total rate (Annuitätendarlehen in German banking). Interest-only: lower monthly payment but you still owe the full loan amount at maturity — common in Dubai short-term cashflow plays."
                    >
                      <select
                        data-testid="amortization"
                        value={draft.amortization}
                        onChange={(e) => set("amortization", e.target.value)}
                        disabled={isConverted}
                        className={inputCls}
                      >
                        <option value="amortizing">Amortizing</option>
                        <option value="interest_only">Interest-only</option>
                      </select>
                    </Field>
                  </div>
                )}

                {draft.use_financing && (
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 pt-3 border-t border-white/[0.08]">
                    <Stat label="Equity required" value={`${sym} ${fmt(result.downPayment)}`} />
                    <Stat label="Monthly payment" value={`${sym} ${fmt(result.monthlyDebtService, 0)}`} highlight />
                    <Stat label="Total cash required" value={`${sym} ${fmt(result.cashInvested)}`} highlight />
                  </div>
                )}
              </Section>

              {/* Section: Income */}
              <Section title="Rental Income" icon={isStr ? Bed : CalendarRange}>
                <div className="flex items-center gap-2 mb-4 bg-white/[0.02] p-1 inline-flex">
                  <button
                    type="button"
                    data-testid="income-mode-long"
                    onClick={() => set("income_mode", "long_term")}
                    disabled={isConverted}
                    className={`px-3 py-1.5 text-xs tracking-wider uppercase transition-colors ${
                      !isStr ? "bg-[#D4AF37] text-black" : "text-white/65 hover:text-white"
                    }`}
                  >
                    Long-term
                  </button>
                  <button
                    type="button"
                    data-testid="income-mode-short"
                    onClick={() => set("income_mode", "short_term")}
                    disabled={isConverted}
                    className={`px-3 py-1.5 text-xs tracking-wider uppercase transition-colors ${
                      isStr ? "bg-[#D4AF37] text-black" : "text-white/65 hover:text-white"
                    }`}
                  >
                    Short-term (STR)
                  </button>
                </div>

                {!isStr ? (
                  <div>
                    <MoneyField testId="monthly-rent" sym={sym} label="Monthly rent" value={draft.monthly_rent} onChange={(v) => set("monthly_rent", v)} disabled={isConverted} />
                    <p className="text-white/65 text-[10px] mt-2 leading-relaxed">
                      Long-term assumes 100% utilisation. If you expect void periods between tenants, reduce the monthly rent accordingly.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <MoneyField testId="nightly-rate" sym={sym} label="Nightly rate (avg)" value={draft.nightly_rate} onChange={(v) => set("nightly_rate", v)} disabled={isConverted} />
                    <Field label={`Occupancy: ${draft.occupancy_pct}%`}>
                      <input
                        type="range"
                        data-testid="occupancy-pct"
                        min={0} max={100} step={1}
                        value={draft.occupancy_pct}
                        onChange={(e) => set("occupancy_pct", parseFloat(e.target.value))}
                        disabled={isConverted}
                        className="w-full accent-[#D4AF37]"
                      />
                    </Field>
                  </div>
                )}

                <div className="mt-3 grid grid-cols-2 gap-3 pt-3 border-t border-white/[0.08]">
                  <Stat label="Annual gross income" value={`${sym} ${fmt(result.annualGrossIncome)}`} />
                  <Stat label="Monthly gross income" value={`${sym} ${fmt(result.monthlyGrossIncome)}`} />
                </div>
              </Section>

              {/* Section: Operating Expenses */}
              <Section title="Operating Expenses" icon={Briefcase}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {isGermany ? (
                    <>
                      <MoneyField
                        testId="hausgeld-umlage"
                        sym={sym}
                        label="Hausgeld umlagefähig / mo"
                        tip="Umlagefähige Nebenkosten gemäß §556a BGB / Betriebskostenverordnung (Heizung, Wasser, Müll, Hausmeister, Gemeinschaftsstrom, anteilige Gebäudeversicherung u.ä.). Werden über die Nebenkostenabrechnung an den Mieter weitergegeben — fließen daher NICHT in deine Running Costs."
                        value={draft.hausgeld_umlage_monthly}
                        onChange={(v) => set("hausgeld_umlage_monthly", v)}
                        disabled={isConverted}
                      />
                      <MoneyField
                        testId="hausgeld-nicht-umlage"
                        sym={sym}
                        label="Hausgeld nicht-umlagefähig / mo"
                        tip="Nicht-umlagefähige Hausgeld-Anteile (Hausverwaltung, anteilige Reparaturen im Sondereigentum, Kontoführung der WEG u.ä.). Trägt der Eigentümer selbst — werden in die jährlichen Running Costs eingerechnet."
                        value={draft.hausgeld_nicht_umlage_monthly}
                        onChange={(v) => set("hausgeld_nicht_umlage_monthly", v)}
                        disabled={isConverted}
                      />
                      <MoneyField
                        testId="ruecklage"
                        sym={sym}
                        label="Instandhaltungsrücklage / mo"
                        tip="Rücklage für künftige Reparaturen am Sondereigentum (Heizung, Bad, Bodenbelag etc.). Wird vom Eigentümer monatlich aufgebaut — fließt in die Running Costs ein. Richtwert: 0,5–1,0% der Anschaffungskosten p.a. (≈ €30–100/Monat bei einer typischen ETW)."
                        value={draft.instandhaltungsruecklage_monthly}
                        onChange={(v) => set("instandhaltungsruecklage_monthly", v)}
                        disabled={isConverted}
                      />
                    </>
                  ) : (
                    <MonthlyOrAnnualMoneyField
                      testId="service-charges"
                      sym={sym}
                      labelMonthly="Service charges (monthly)"
                      labelAnnual="Service charges (annual)"
                      location={draft.location}
                      annualValue={draft.service_charges_annual}
                      onChangeAnnual={(v) => set("service_charges_annual", v)}
                      disabled={isConverted}
                    />
                  )}
                  <MonthlyOrAnnualMoneyField
                    testId="insurance"
                    sym={sym}
                    labelMonthly="Insurance (monthly)"
                    labelAnnual="Insurance (annual)"
                    location={draft.location}
                    annualValue={draft.insurance_annual}
                    onChangeAnnual={(v) => set("insurance_annual", v)}
                    disabled={isConverted}
                  />
                  <MoneyField
                    testId="mgmt-fee"
                    sym={sym}
                    label="Management fee (annual)"
                    tip="Annual fee paid to a property manager. Typical: 8–12% of gross rent for long-term lets, 15–25% for short-term/Airbnb. Enter 0 if you self-manage."
                    value={draft.management_fee_annual}
                    onChange={(v) => set("management_fee_annual", v)}
                    disabled={isConverted}
                  />
                  <MoneyField
                    testId="maint-reserve"
                    sym={sym}
                    label="Maintenance reserve (annual)"
                    tip="Annual reserve set aside for repairs and replacements (boiler, paint, appliances). Rule of thumb: 1–2% of purchase price for ready properties, 0.5–1% for new builds."
                    value={draft.maintenance_reserve_annual}
                    onChange={(v) => set("maintenance_reserve_annual", v)}
                    disabled={isConverted}
                  />
                  <NumberField
                    testId="vacancy-reserve"
                    suffix="%"
                    label="Vacancy reserve"
                    tip="Annual reserve for void periods between tenants, expressed as % of gross rent. German industry standard: 2% (Mietausfallwagnis). Set higher for short-term lets or high tenant turnover areas."
                    value={draft.vacancy_reserve_pct}
                    onChange={(v) => set("vacancy_reserve_pct", v)}
                    step={0.1}
                    disabled={isConverted}
                  />
                  {isStr && <MoneyField testId="utilities" sym={sym} label="Utilities (annual)" value={draft.utilities_annual} onChange={(v) => set("utilities_annual", v)} disabled={isConverted} />}
                  {isStr && <MoneyField testId="cleaning-monthly" sym={sym} label="Cleaning costs (monthly)" value={draft.cleaning_monthly} onChange={(v) => set("cleaning_monthly", v)} disabled={isConverted} />}
                </div>
              </Section>

              {/* Notes */}
              <Section title="Notes" icon={FileText}>
                <textarea
                  data-testid="deal-notes"
                  value={draft.notes || ""}
                  onChange={(e) => set("notes", e.target.value)}
                  disabled={isConverted}
                  rows={3}
                  className={`${inputCls} w-full resize-y`}
                  placeholder="Comparables, agent contact, viewing notes…"
                />
              </Section>
            </div>

            {/* Results panel */}
            <ResultsPanel
              draft={draft}
              result={result}
              warnings={warnings}
              sym={sym}
              isStr={isStr}
              isConverted={isConverted}
              onConvert={() => setConfirmConvert(true)}
              converting={converting}
            />
          </div>
        </div>
      )}

      {/* Confirm dialogs */}
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title="Delete this deal?"
        message="This permanently removes the draft. The math is gone."
        confirmLabel="Delete"
        danger
      />
      <ConfirmDialog
        open={confirmConvert}
        onClose={() => setConfirmConvert(false)}
        onConfirm={handleConvert}
        title="Convert to Asset?"
        message={`This will create a new asset "${draft.name}" in your portfolio with the data from this deal. The deal will be archived but kept for reference.`}
        confirmLabel={converting ? "Converting…" : "Convert"}
      />
    </div>
    </TooltipProvider>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

const inputCls =
  "bg-[#0A0A0A] border border-white/[0.12] focus:border-[#D4AF37]/40 text-white text-sm px-3 py-2 outline-none w-full font-sans transition-colors disabled:opacity-40 disabled:cursor-not-allowed";

function Field({ label, tip, children }) {
  return (
    <div>
      <label className="flex items-center gap-1.5 text-[10px] tracking-wider uppercase text-white/60 mb-1.5 font-sans">
        <span>{label}</span>
        {tip && <InfoTip text={tip} />}
      </label>
      {children}
    </div>
  );
}

function InfoTip({ text }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          tabIndex={-1}
          className="text-white/65 hover:text-[#D4AF37] transition-colors leading-none"
          onClick={(e) => e.preventDefault()}
          aria-label="More info"
        >
          <HelpCircle className="w-3 h-3" strokeWidth={1.8} />
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className="max-w-[280px] bg-[#0A0A0A] border border-[#D4AF37]/30 text-white/85 text-[11px] leading-relaxed font-sans normal-case tracking-normal whitespace-normal py-2.5 px-3"
      >
        {text}
      </TooltipContent>
    </Tooltip>
  );
}


/**
 * German-specific closing-cost block. Renders 4 editable fields:
 *   1. Bundesland selector — auto-fills the GrESt rate when changed
 *   2. Grunderwerbsteuer % (editable, pre-filled from Bundesland)
 *   3. Notar + Grundbuch % (editable, default 1.6%)
 *   4. Maklerprovision % (Käuferanteil, editable, default 3.57%)
 *
 * The aggregate (sum × purchase_price) is kept in sync with the canonical
 * `closing_costs` field so all downstream math (TPC, loan, yields) stays
 * consistent with non-German deals.
 */
function GermanClosingCosts({ draft, set, disabled }) {
  const purchase = Number(draft.purchase_price) || 0;

  // Whenever a rate changes, recompute the aggregate closing_costs (€).
  const syncTotal = (overrides = {}) => {
    const grest  = parseFloat(overrides.de_grest_pct  ?? draft.de_grest_pct)  || 0;
    const notar  = parseFloat(overrides.de_notar_pct  ?? draft.de_notar_pct)  || 0;
    const makler = parseFloat(overrides.de_makler_pct ?? draft.de_makler_pct) || 0;
    const total = (purchase * (grest + notar + makler)) / 100;
    set("closing_costs", Math.round(total * 100) / 100);
  };

  // Keep aggregate fresh if purchase price changes (no infinite loop because
  // the user-edited fields don't change unless the user types in them).
  useEffect(() => {
    syncTotal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchase]);

  const onBundesland = (code) => {
    const bl = DE_BUNDESLAENDER.find((b) => b.code === code);
    if (!bl) return;
    set("de_bundesland", code);
    set("de_grest_pct", bl.grest);
    syncTotal({ de_grest_pct: bl.grest });
  };

  const grestEuro  = (purchase * (parseFloat(draft.de_grest_pct)  || 0)) / 100;
  const notarEuro  = (purchase * (parseFloat(draft.de_notar_pct)  || 0)) / 100;
  const maklerEuro = (purchase * (parseFloat(draft.de_makler_pct) || 0)) / 100;
  const totalEuro  = grestEuro + notarEuro + maklerEuro;

  return (
    <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field
        label="Bundesland"
        tip="Bestimmt die Grunderwerbsteuer. Wählst du ein Bundesland, wird der aktuelle GrESt-Satz (Stand 2026) automatisch übernommen — manuell überschreibbar."
      >
        <select
          data-testid="de-bundesland"
          value={draft.de_bundesland || "bayern"}
          onChange={(e) => onBundesland(e.target.value)}
          disabled={disabled}
          className={inputCls}
        >
          {DE_BUNDESLAENDER.map((b) => (
            <option key={b.code} value={b.code}>
              {b.label} · {b.grest}%
            </option>
          ))}
        </select>
      </Field>

      <NumberField
        testId="de-grest"
        suffix="%"
        label="Grunderwerbsteuer"
        tip="Vom Kaufpreis (Stand 2026 je Bundesland). Default folgt dem oben gewählten Bundesland, du kannst aber jederzeit einen abweichenden Satz eintragen."
        value={draft.de_grest_pct}
        onChange={(v) => { set("de_grest_pct", v); syncTotal({ de_grest_pct: v }); }}
        step={0.1}
        disabled={disabled}
      />

      <NumberField
        testId="de-notar"
        suffix="%"
        label="Notar + Grundbuch"
        tip="Notarkosten + Grundbucheintragung. Üblicher Richtwert: 1,5–2,0%. Default 1,6%."
        value={draft.de_notar_pct}
        onChange={(v) => { set("de_notar_pct", v); syncTotal({ de_notar_pct: v }); }}
        step={0.1}
        disabled={disabled}
      />

      <NumberField
        testId="de-makler"
        suffix="%"
        label="Maklerprovision (Käuferanteil)"
        tip="Käuferanteil seit dem Bestellerprinzip 2020 max. 50% der Gesamtprovision. Marktüblich ~3,57% inkl. MwSt., bei Eigentumswohnungen oft niedriger. Setze 0%, wenn keine Maklerprovision anfällt."
        value={draft.de_makler_pct}
        onChange={(v) => { set("de_makler_pct", v); syncTotal({ de_makler_pct: v }); }}
        step={0.01}
        disabled={disabled}
      />

      <div className="sm:col-span-2 bg-white/[0.02] border border-white/[0.10] px-3 py-2 text-[11px]">
        <div className="flex items-center justify-between text-white/60">
          <span>Grunderwerbsteuer</span><span className="font-mono">€ {fmt(grestEuro)}</span>
        </div>
        <div className="flex items-center justify-between text-white/60">
          <span>Notar &amp; Grundbuch</span><span className="font-mono">€ {fmt(notarEuro)}</span>
        </div>
        <div className="flex items-center justify-between text-white/60">
          <span>Maklerprovision</span><span className="font-mono">€ {fmt(maklerEuro)}</span>
        </div>
        <div className="flex items-center justify-between text-white pt-1 mt-1 border-t border-white/[0.10] font-medium">
          <span>Nebenkosten gesamt</span>
          <span className="font-mono" data-testid="de-closing-total">€ {fmt(totalEuro)}</span>
        </div>
      </div>
    </div>
  );
}


/**
 * Dubai-specific closing-cost block (Feb 2026 — Excel parity).
 *
 * Replaces the generic "Closing costs" field with the same line-items the
 * user's reference Excel uses, so the numbers tie out to the cent:
 *   1. DLD Property Transfer Fee  — auto, 4% of purchase price (gesetzlich)
 *   2. Buyer's Commission         — auto, 2.1% (2% + 5% VAT, marktüblich)
 *   3. Trustee Office Fee         — auto, tiered (2,100 < 2M else 4,200 AED)
 *   4. Möblierung (furniture)     — user input
 *   5. Sonstige Finanzierungskosten — user input (one-time financing setup)
 *   6. Scheckerstellungsgebühr    — toggle + amount (default 5,000 AED)
 *   7. Geldtransfer (Wire)        — user input (no auto-calc)
 *
 * The aggregate is written into `closing_costs` so all downstream math
 * (TPC, LTV, yields) continues to flow through the existing pipeline.
 * Möblierung / Cheque are ALSO tracked separately so dealMath can apply
 * the Excel-style "Nettorendite excludes furniture" rule.
 */
function DubaiClosingCosts({ draft, set, sym, disabled }) {
  const purchase = Number(draft.purchase_price) || 0;
  const furniture = Number(draft.furniture_cost) || 0;
  const financingSetup = Number(draft.financing_setup_costs) || 0;
  const chequeFee = Number(draft.cheque_fee) || 0;
  // Wire transfer is now strictly user-entered (Feb 28, 2026). Empty → 0.
  const wireOverrideRaw = draft.wire_transfer_fee;
  const hasWireOverride = wireOverrideRaw !== undefined && wireOverrideRaw !== null && wireOverrideRaw !== "";

  // ── Auto-computed fixed Dubai costs ────────────────────────────────────
  const dldAuto = purchase * 0.04;
  const brokerAuto = purchase * 0.021;
  const trusteeAuto = purchase >= 2_000_000 ? 4_200 : 2_100;
  // Per-line manual overrides — empty/null/undefined → use the auto-calc.
  const hasOverride = (v) => v !== undefined && v !== null && v !== "";
  const dldOverrideRaw = draft.dld_fee_override;
  const brokerOverrideRaw = draft.broker_override;
  const trusteeOverrideRaw = draft.trustee_override;
  const dldFee = hasOverride(dldOverrideRaw) ? Number(dldOverrideRaw) : dldAuto;
  const brokerFee = hasOverride(brokerOverrideRaw) ? Number(brokerOverrideRaw) : brokerAuto;
  const trusteeFee = hasOverride(trusteeOverrideRaw) ? Number(trusteeOverrideRaw) : trusteeAuto;
  // Excel "anschaffungsnebenkosten" base — excludes Geldtransfer and Cheque
  const baseClosing =
    dldFee + brokerFee + trusteeFee + furniture + financingSetup;
  const geldtransfer = hasWireOverride ? Number(wireOverrideRaw) : 0;
  const totalClosing = baseClosing + geldtransfer + chequeFee;

  // Keep canonical `closing_costs` in sync so TPC / loan / yield calcs work.
  useEffect(() => {
    set("closing_costs", Math.round(totalClosing * 100) / 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchase, furniture, financingSetup, chequeFee, wireOverrideRaw, dldOverrideRaw, brokerOverrideRaw, trusteeOverrideRaw]);

  return (
    <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
      <MoneyField
        testId="dubai-furniture"
        sym={sym}
        label="Furniture"
        tip="Optional furniture cost. Included in the total investment, but excluded from the Net Yield denominator because long-term tenants typically bring their own — so the furniture spend doesn't earn rent."
        value={draft.furniture_cost}
        onChange={(v) => set("furniture_cost", v)}
        disabled={disabled}
      />

      <MoneyField
        testId="dubai-financing-setup"
        sym={sym}
        label="Other financing setup costs"
        tip="One-time financing setup fees (bank arrangement, valuation, processing). Distinct from interest/principal — these are paid up front, not over the loan term."
        value={draft.financing_setup_costs}
        onChange={(v) => set("financing_setup_costs", v)}
        disabled={disabled}
      />

      <MoneyField
        testId="dubai-cheque-fee"
        sym={sym}
        label="Cheque issuance fee"
        tip="5,000 AED — applies only if you don't have a UAE bank account and need a manager's cheque to settle the purchase. Otherwise leave at 0."
        value={draft.cheque_fee}
        onChange={(v) => set("cheque_fee", v)}
        disabled={disabled}
      />

      <Field
        label="Wire transfer fee"
        tip="Type the exact AED amount your bank charged for funding the purchase (SWIFT + FX margin when sending capital from abroad). Leave at 0 if paying from an in-country AED account — there is no automatic estimate."
      >
        <div className="flex items-center bg-[#0A0A0A] border border-white/[0.12] focus-within:border-[#D4AF37]/40">
          <span className="text-white/60 text-xs px-3 select-none">{sym}</span>
          <input
            data-testid="dubai-wire-fee"
            type="number"
            step="any"
            min="0"
            value={hasWireOverride ? wireOverrideRaw : ""}
            onChange={(e) => set("wire_transfer_fee", e.target.value)}
            disabled={disabled}
            placeholder="0"
            className="bg-transparent text-white text-sm pr-3 py-2 outline-none w-full font-sans disabled:opacity-40"
          />
        </div>
      </Field>

      <div className="sm:col-span-2 bg-white/[0.02] border border-white/[0.10] px-3 py-2 text-[11px]">
        <div className="flex items-center justify-between text-white/60">
          <span>DLD Transfer Fee (4%)</span>
          <InlineEditableAmount
            sym={sym}
            value={dldFee}
            isOverridden={hasOverride(dldOverrideRaw)}
            onChange={(v) => set("dld_fee_override", v)}
            onResetAuto={() => set("dld_fee_override", "")}
            disabled={disabled}
            testId="dubai-dld-fee"
          />
        </div>
        <div className="flex items-center justify-between text-white/60">
          <span>Buyer's Commission (2.1%)</span>
          <InlineEditableAmount
            sym={sym}
            value={brokerFee}
            isOverridden={hasOverride(brokerOverrideRaw)}
            onChange={(v) => set("broker_override", v)}
            onResetAuto={() => set("broker_override", "")}
            disabled={disabled}
            testId="dubai-broker-fee"
          />
        </div>
        <div className="flex items-center justify-between text-white/60">
          <span>Trustee Office Fee</span>
          <InlineEditableAmount
            sym={sym}
            value={trusteeFee}
            isOverridden={hasOverride(trusteeOverrideRaw)}
            onChange={(v) => set("trustee_override", v)}
            onResetAuto={() => set("trustee_override", "")}
            disabled={disabled}
            testId="dubai-trustee-fee"
          />
        </div>
        {furniture > 0 && (
          <div className="flex items-center justify-between text-white/60">
            <span>Furniture</span><span className="font-mono">{sym} {fmt(furniture)}</span>
          </div>
        )}
        {financingSetup > 0 && (
          <div className="flex items-center justify-between text-white/60">
            <span>Other financing setup costs</span><span className="font-mono">{sym} {fmt(financingSetup)}</span>
          </div>
        )}
        <div className="flex items-center justify-between text-white/60 pt-1 mt-1 border-t border-white/[0.08]">
          <span>Acquisition costs subtotal</span><span className="font-mono">{sym} {fmt(baseClosing)}</span>
        </div>
        {geldtransfer > 0 && (
          <div className="flex items-center justify-between text-white/60">
            <span>Wire transfer (manual)</span><span className="font-mono">{sym} {fmt(geldtransfer)}</span>
          </div>
        )}
        {chequeFee > 0 && (
          <div className="flex items-center justify-between text-white/60">
            <span>Cheque issuance fee</span><span className="font-mono">{sym} {fmt(chequeFee)}</span>
          </div>
        )}
        <div className="flex items-center justify-between text-white pt-1 mt-1 border-t border-white/[0.10] font-medium">
          <span>Total closing costs</span>
          <span className="font-mono" data-testid="dubai-closing-total">{sym} {fmt(totalClosing)}</span>
        </div>
      </div>
    </div>
  );
}


/**
 * North Cyprus closing-cost block (Feb 2026 — Excel parity).
 *
 * Mirrors the Nordzypern Immobilien Kalkulator line items:
 *   1. Stempelgebühr      — auto, 0.5% × purchase
 *   2. VAT (KDV)          — auto, 5% × purchase (new builds only)
 *   3. Title Deed Transfer — auto, 12% × purchase (always)
 *   4. Transformer Fee    — fixed GBP, only new builds
 *   5. Solicitor Fee      — fixed GBP
 *   6. Furniture          — user input (excluded from Net Yield denominator)
 *   7. Other financing setup costs — user input (one-time financing fees)
 *   8. Wire transfer      — user input (no auto-calc)
 */
function CyprusClosingCosts({ draft, set, sym, disabled }) {
  const purchase = Number(draft.purchase_price) || 0;
  const isNewBuild = draft.is_new_build !== false;
  const transformerFee = isNewBuild ? Number(draft.transformer_fee) || 0 : 0;
  const solicitorFee = Number(draft.solicitor_fee) || 0;
  const furniture = Number(draft.furniture_cost) || 0;
  const financingSetup = Number(draft.financing_setup_costs) || 0;
  // Wire transfer is now strictly user-entered (Feb 28, 2026). Empty → 0.
  const wireOverrideRaw = draft.wire_transfer_fee;
  const hasWireOverride =
    wireOverrideRaw !== undefined && wireOverrideRaw !== null && wireOverrideRaw !== "";

  const stampDutyAuto = purchase * 0.005;
  const vatAuto = isNewBuild ? purchase * 0.05 : 0;
  const transferFeeAuto = purchase * 0.12;
  // Manual override fields — empty/null/undefined → use the auto-calc.
  // Any explicit number (incl. 0) overrides.
  const hasOverride = (v) => v !== undefined && v !== null && v !== "";
  const stampDutyOverrideRaw = draft.stamp_duty_override;
  const vatOverrideRaw = draft.vat_override;
  const transferFeeOverrideRaw = draft.transfer_fee_override;
  const stampDuty = hasOverride(stampDutyOverrideRaw) ? Number(stampDutyOverrideRaw) : stampDutyAuto;
  const vat = hasOverride(vatOverrideRaw) ? Number(vatOverrideRaw) : vatAuto;
  const transferFee = hasOverride(transferFeeOverrideRaw) ? Number(transferFeeOverrideRaw) : transferFeeAuto;
  const baseClosing =
    stampDuty + vat + transferFee + transformerFee + solicitorFee + furniture + financingSetup;
  const wireFee = hasWireOverride ? Number(wireOverrideRaw) : 0;
  const totalClosing = baseClosing + wireFee;

  useEffect(() => {
    set("closing_costs", Math.round(totalClosing * 100) / 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    purchase, isNewBuild, transformerFee, solicitorFee, furniture,
    financingSetup, wireOverrideRaw,
    stampDutyOverrideRaw, vatOverrideRaw, transferFeeOverrideRaw,
  ]);

  return (
    <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field
        label="Build type (controls VAT + transformer)"
        tip="New builds incur 5% VAT (KDV) and a one-time transformer fee for electricity connection; resale properties do not."
      >
        <select
          data-testid="cyprus-build-type"
          value={isNewBuild ? "new" : "resale"}
          onChange={(e) => set("is_new_build", e.target.value === "new")}
          disabled={disabled}
          className={inputCls}
        >
          <option value="new">New build</option>
          <option value="resale">Resale / off-plan handover</option>
        </select>
      </Field>

      <MoneyField
        testId="cyprus-transformer-fee"
        sym={sym}
        label="Transformer fee"
        tip="One-time fee for electricity transformer / connection. Charged by developers on new builds. Typical: 1,500–3,000 GBP."
        value={draft.transformer_fee}
        onChange={(v) => set("transformer_fee", v)}
        disabled={disabled || !isNewBuild}
      />

      <MoneyField
        testId="cyprus-solicitor-fee"
        sym={sym}
        label="Solicitor fee"
        tip="TRNC legal representation. Required for every foreign buyer (Aliens' Permission, Land Registry, contract). Typical: 1,500–3,000 GBP fixed."
        value={draft.solicitor_fee}
        onChange={(v) => set("solicitor_fee", v)}
        disabled={disabled}
      />

      <MoneyField
        testId="cyprus-furniture"
        sym={sym}
        label="Furniture"
        tip="Optional furniture cost. Included in the total investment, but excluded from the Net Yield denominator because long-term tenants typically bring their own."
        value={draft.furniture_cost}
        onChange={(v) => set("furniture_cost", v)}
        disabled={disabled}
      />

      <MoneyField
        testId="cyprus-financing-setup"
        sym={sym}
        label="Other financing setup costs"
        tip="One-time financing setup fees (developer payment plan admin, mortgage arrangement, valuation, processing)."
        value={draft.financing_setup_costs}
        onChange={(v) => set("financing_setup_costs", v)}
        disabled={disabled}
      />

      <Field
        label="Wire transfer fee"
        tip="Type the exact GBP amount your bank charged for funding the purchase. Leave at 0 if not applicable — there is no automatic estimate."
      >
        <div className="flex items-center bg-[#0A0A0A] border border-white/[0.12] focus-within:border-[#D4AF37]/40">
          <span className="text-white/60 text-xs px-3 select-none">{sym}</span>
          <input
            data-testid="cyprus-wire-fee"
            type="number"
            step="any"
            min="0"
            value={hasWireOverride ? wireOverrideRaw : ""}
            onChange={(e) => set("wire_transfer_fee", e.target.value)}
            disabled={disabled}
            placeholder="0"
            className="bg-transparent text-white text-sm pr-3 py-2 outline-none w-full font-sans disabled:opacity-40"
          />
        </div>
      </Field>

      <div className="sm:col-span-2 bg-white/[0.02] border border-white/[0.10] px-3 py-2 text-[11px]">
        <div className="flex items-center justify-between text-white/60">
          <span>Stamp Duty (0.5%)</span>
          <InlineEditableAmount
            sym={sym}
            value={stampDuty}
            isOverridden={hasOverride(stampDutyOverrideRaw)}
            onChange={(v) => set("stamp_duty_override", v)}
            onResetAuto={() => set("stamp_duty_override", "")}
            disabled={disabled}
            testId="cyprus-stamp-duty"
          />
        </div>
        {isNewBuild && (
          <div className="flex items-center justify-between text-white/60">
            <span>VAT / KDV (5%)</span>
            <InlineEditableAmount
              sym={sym}
              value={vat}
              isOverridden={hasOverride(vatOverrideRaw)}
              onChange={(v) => set("vat_override", v)}
              onResetAuto={() => set("vat_override", "")}
              disabled={disabled}
              testId="cyprus-vat"
            />
          </div>
        )}
        <div className="flex items-center justify-between text-white/60">
          <span>Title Deed Transfer (12%)</span>
          <InlineEditableAmount
            sym={sym}
            value={transferFee}
            isOverridden={hasOverride(transferFeeOverrideRaw)}
            onChange={(v) => set("transfer_fee_override", v)}
            onResetAuto={() => set("transfer_fee_override", "")}
            disabled={disabled}
            testId="cyprus-title-transfer"
          />
        </div>
        {transformerFee > 0 && (
          <div className="flex items-center justify-between text-white/60">
            <span>Transformer fee</span><span className="font-mono">{sym} {fmt(transformerFee)}</span>
          </div>
        )}
        {solicitorFee > 0 && (
          <div className="flex items-center justify-between text-white/60">
            <span>Solicitor fee</span><span className="font-mono">{sym} {fmt(solicitorFee)}</span>
          </div>
        )}
        {furniture > 0 && (
          <div className="flex items-center justify-between text-white/60">
            <span>Furniture</span><span className="font-mono">{sym} {fmt(furniture)}</span>
          </div>
        )}
        {financingSetup > 0 && (
          <div className="flex items-center justify-between text-white/60">
            <span>Other financing setup costs</span><span className="font-mono">{sym} {fmt(financingSetup)}</span>
          </div>
        )}
        <div className="flex items-center justify-between text-white/60 pt-1 mt-1 border-t border-white/[0.08]">
          <span>Acquisition costs subtotal</span><span className="font-mono">{sym} {fmt(baseClosing)}</span>
        </div>
        {wireFee > 0 && (
          <div className="flex items-center justify-between text-white/60">
            <span>Wire transfer (manual)</span>
            <span className="font-mono">{sym} {fmt(wireFee)}</span>
          </div>
        )}
        <div className="flex items-center justify-between text-white pt-1 mt-1 border-t border-white/[0.10] font-medium">
          <span>Total closing costs</span>
          <span className="font-mono" data-testid="cyprus-closing-total">{sym} {fmt(totalClosing)}</span>
        </div>
      </div>
    </div>
  );
}




/**
 * Inline-editable amount displayed in the closing-cost breakdown.
 * Click the number → swap to a small input. Enter/blur commits, Escape cancels.
 * Empty input commits as `""` (= "use auto-default"). A small reset arrow shows
 * when an override is active.
 */
function InlineEditableAmount({ value, onChange, sym, disabled, testId, isOverridden, onResetAuto }) {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = (raw) => {
    const cleaned = (raw ?? "").trim();
    onChange(cleaned === "" ? "" : cleaned);
    setEditing(false);
  };

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1">
        <span className="text-white/60 text-[11px]">{sym}</span>
        <input
          ref={inputRef}
          type="number"
          step="any"
          defaultValue={isOverridden ? value : ""}
          placeholder="auto"
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit(e.target.value);
            if (e.key === "Escape") setEditing(false);
          }}
          className="bg-[#0A0A0A] border border-[#D4AF37]/40 text-white text-[11px] font-mono w-24 px-1.5 py-0.5 outline-none text-right"
          data-testid={testId ? `${testId}-input` : undefined}
        />
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => !disabled && setEditing(true)}
        disabled={disabled}
        title={isOverridden ? "Click to edit (manual override)" : "Click to edit"}
        data-testid={testId}
        className={`font-mono cursor-pointer hover:text-[#D4AF37] transition-colors disabled:cursor-not-allowed disabled:hover:text-inherit ${isOverridden ? "text-[#D4AF37]" : ""}`}
      >
        {sym} {fmt(value)}
      </button>
      {isOverridden && !disabled && onResetAuto && (
        <button
          type="button"
          onClick={onResetAuto}
          title="Reset to auto"
          className="text-white/65 hover:text-white/90 text-[10px] leading-none px-1"
          data-testid={testId ? `${testId}-reset` : undefined}
        >
          ↺
        </button>
      )}
    </span>
  );
}

function MoneyField({ label, value, onChange, sym, disabled, testId }) {
  return (
    <Field label={label}>
      <div className="flex items-center bg-[#0A0A0A] border border-white/[0.12] focus-within:border-[#D4AF37]/40">
        <span className="text-white/60 text-xs px-3 select-none">{sym}</span>
        <input
          type="number"
          data-testid={testId}
          value={value || ""}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          disabled={disabled}
          step="any"
          min={0}
          className="bg-transparent text-white text-sm pr-3 py-2 outline-none w-full font-sans disabled:opacity-40"
        />
      </div>
    </Field>
  );
}

/**
 * Money input whose UNIT depends on location:
 *   - Germany / North Cyprus → monthly (Hausgeld / common-charge mindset)
 *   - Dubai (and others)     → annual  (AED/sqft/yr quoted)
 *
 * Storage stays annual on every field for consistency in the math engine;
 * we only convert at the UI boundary.
 */
function MonthlyOrAnnualMoneyField({
  labelMonthly, labelAnnual, annualValue, onChangeAnnual, location, sym, disabled, testId,
}) {
  const showMonthly = MONTHLY_OPEX_LOCATIONS.has(location);
  const displayed = showMonthly ? (annualValue || 0) / 12 : (annualValue || 0);
  const onChangeDisplayed = (v) => onChangeAnnual(showMonthly ? v * 12 : v);
  return (
    <MoneyField
      label={showMonthly ? labelMonthly : labelAnnual}
      value={displayed}
      onChange={onChangeDisplayed}
      sym={sym}
      disabled={disabled}
      testId={testId}
    />
  );
}

function NumberField({ label, value, onChange, suffix, step = 1, disabled, testId, tip }) {
  return (
    <Field label={label} tip={tip}>
      <div className="flex items-center bg-[#0A0A0A] border border-white/[0.12] focus-within:border-[#D4AF37]/40">
        <input
          type="number"
          data-testid={testId}
          value={value || ""}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          disabled={disabled}
          step={step}
          min={0}
          className="bg-transparent text-white text-sm px-3 py-2 outline-none w-full font-sans disabled:opacity-40"
        />
        {suffix && <span className="text-white/60 text-xs px-3 select-none">{suffix}</span>}
      </div>
    </Field>
  );
}

function Section({ title, icon: Icon, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-white/[0.10] bg-white/[0.01]" data-testid={`section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-white/[0.02] transition-colors"
      >
        <div className="flex items-center gap-2.5">
          {Icon && <Icon className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />}
          <span className="text-white text-sm font-medium tracking-wide">{title}</span>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-white/65" /> : <ChevronRightIcon className="w-4 h-4 text-white/65" />}
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-1">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Stat({ label, value, highlight = false }) {
  return (
    <div>
      <p className="text-[9px] tracking-wider uppercase text-white/55 mb-0.5">{label}</p>
      <p className={`text-sm tabular-nums ${highlight ? "text-[#D4AF37]" : "text-white"}`}>{value}</p>
    </div>
  );
}

function ToolbarBtn({ icon: Icon, label, onClick, loading, danger, disabled, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      data-testid={testId}
      title={label}
      className={`px-2.5 py-1.5 text-[10px] tracking-wider uppercase inline-flex items-center gap-1.5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
        danger ? "text-red-300 hover:text-red-200 hover:bg-red-500/10" : "text-white/85 hover:text-white hover:bg-white/[0.04]"
      }`}
    >
      <Icon className="w-3.5 h-3.5" strokeWidth={1.6} />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

function DealsSidebar({ deals, activeId, onSelect, showConverted, onToggleConverted, hasConverted }) {
  return (
    <div className="space-y-2">
      <p className="text-[10px] tracking-[0.2em] uppercase text-white/65 px-2">Your Drafts</p>
      <div className="space-y-1">
        {deals.map((d) => (
          <button
            key={d.id}
            type="button"
            onClick={() => onSelect(d)}
            data-testid={`deal-row-${d.id}`}
            className={`w-full text-left px-3 py-2.5 border-l-2 transition-colors ${
              activeId === d.id
                ? "border-[#D4AF37] bg-[#D4AF37]/[0.04]"
                : "border-transparent hover:bg-white/[0.02]"
            }`}
          >
            <p className="text-white text-sm truncate">{d.name}</p>
            <p className="text-white/65 text-[10px] uppercase tracking-wider mt-0.5">
              {d.location?.replace("_", " ")} · {d.currency}
              {d.converted_to_asset_id && (
                <span className="ml-2 text-emerald-400">· converted</span>
              )}
            </p>
          </button>
        ))}
      </div>
      {hasConverted && (
        <button
          type="button"
          onClick={onToggleConverted}
          className="text-white/65 hover:text-white/85 text-[10px] uppercase tracking-wider px-2 mt-3 transition-colors"
        >
          {showConverted ? "Hide converted" : "Show converted"}
        </button>
      )}
    </div>
  );
}

function ResultsPanel({ draft, result, warnings, sym, isStr, isConverted, onConvert, converting }) {
  const cashflowPositive = result.monthlyCashflow >= 0;
  const cashflowColor = cashflowPositive ? "text-emerald-400" : "text-red-400";

  return (
    <div className="space-y-3">
      {/* Headline */}
      <div className="bg-[#0A0A0A] border border-[#D4AF37]/15 p-5">
        <div className="flex items-center gap-2 mb-2">
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37]">Monthly Cashflow</p>
          <InfoTip text="(Gross rental income − operating costs − interest − principal) ÷ 12" />
        </div>
        <p className={`font-serif text-4xl ${cashflowColor} tabular-nums`} data-testid="result-monthly-cashflow">
          {cashflowPositive ? "+" : ""}{sym} {fmt(result.monthlyCashflow)}
        </p>
        <p className="text-white/60 text-xs mt-1">
          Monatlicher Überschuss · {draft.use_financing ? "nach Zinsen + Tilgung" : "ohne Finanzierung"}
        </p>
      </div>

      {/* Key metrics grid */}
      <div className="grid grid-cols-2 gap-2">
        <MetricCard
          label="Gross ROI"
          value={fmtPct(result.grossRoiPct)}
          subtitle="Bruttorendite"
          testId="result-gross-roi"
          tip="Gross rental income p.a. ÷ purchase price × 100"
        />
        <MetricCard
          label="Net Yield"
          value={fmtPct(result.netYieldPct)}
          subtitle="Nettorendite"
          testId="result-net-yield"
          tip="(Gross rental income − operating costs) ÷ total investment × 100"
        />
        <MetricCard
          label="Equity Yield"
          value={fmtPct(result.equityYieldPct ?? 0)}
          subtitle="Eigenkapitalrendite"
          testId="result-equity-yield"
          highlight={Math.abs(result.equityYieldPct ?? 0) > 0.01}
          tip="(Gross rental income − operating costs − interest) ÷ equity × 100  ·  Principal repayment is NOT subtracted — it's wealth-building, not a cost."
        />
        <MetricCard
          label={draft.use_financing ? "DSCR" : "Gross Yield"}
          value={draft.use_financing
            ? (result.dscr !== null ? result.dscr.toFixed(2) : "—")
            : fmtPct(result.grossYieldOnPurchasePct)}
          subtitle={draft.use_financing ? "Schuldendienstdeckungsgrad" : "Bruttomietrendite"}
          testId="result-dscr-or-yield"
          tip={draft.use_financing
            ? "Net Operating Income (NOI) ÷ annual debt service  ·  > 1.0 means rent covers the full loan installment."
            : "Gross rental income p.a. ÷ purchase price × 100"}
        />
      </div>

      {/* Income statement breakdown */}
      <div className="bg-white/[0.02] border border-white/[0.10] p-4 space-y-2">
        <p className="text-[10px] tracking-[0.2em] uppercase text-white/60 mb-2">Annual Income Statement</p>
        <Row label="Gross income" value={`${sym} ${fmt(result.annualGrossIncome)}`} />
        {draft.location === "germany" ? (
          <>
            <Row label="− Hausgeld nicht-umlagefähig" value={`(${sym} ${fmt(result.hausgeldNichtUmlageAnnual)})`} muted />
            <Row label="− Instandhaltungsrücklage" value={`(${sym} ${fmt(result.ruecklageAnnual)})`} muted />
            {result.hausgeldUmlageAnnual > 0 && (
              <Row
                label="    Hausgeld umlagefähig (tenant pays)"
                value={`${sym} ${fmt(result.hausgeldUmlageAnnual)}`}
                faint
              />
            )}
          </>
        ) : (
          <Row label="− Service charges" value={`(${sym} ${fmt(result.serviceCharges)})`} muted />
        )}
        <Row label="− Insurance" value={`(${sym} ${fmt(result.insurance)})`} muted />
        <Row label="− Management fee" value={`(${sym} ${fmt(result.managementFee)})`} muted />
        <Row label="− Maintenance reserve" value={`(${sym} ${fmt(result.maintenanceReserve)})`} muted />
        <Row label={`− Vacancy reserve (${result.vacancyPct}%)`} value={`(${sym} ${fmt(result.vacancyReserve)})`} muted />
        {isStr && result.utilitiesAnnual > 0 && (
          <Row label="− Utilities" value={`(${sym} ${fmt(result.utilitiesAnnual)})`} muted />
        )}
        {isStr && result.cleaningAnnual > 0 && (
          <Row label="− Cleaning (annual)" value={`(${sym} ${fmt(result.cleaningAnnual)})`} muted />
        )}
        <div className="border-t border-white/[0.10] pt-2 mt-2">
          <Row label="NOI (Net Operating Income)" value={`${sym} ${fmt(result.noi)}`} bold />
        </div>
        {draft.use_financing && (
          <>
            <Row label="− Debt service" value={`(${sym} ${fmt(result.annualDebtService)})`} muted />
            <Row label="    of which interest" value={`${sym} ${fmt(result.annualInterest)}`} faint />
            <Row label="    of which principal" value={`${sym} ${fmt(result.annualPrincipal)}`} faint />
          </>
        )}
        <div className="border-t border-white/[0.10] pt-2">
          <Row label="Annual cashflow" value={`${sym} ${fmt(result.annualCashflow)}`} bold highlight={cashflowPositive} />
        </div>
        {draft.use_financing && result.annualPrincipal > 0 && (
          <p className="text-white/65 text-[10px] mt-2 leading-relaxed">
            Note: principal repayment ({sym} {fmt(result.annualPrincipal)}/yr) is NOT a cost — it stays as equity buildup in the property. That's why Equity Yield is calculated against interest only, not full debt service. (Annuitätendarlehen convention.)
          </p>
        )}
      </div>

      {/* STR break-even */}
      {isStr && result.breakEvenOccupancyPct !== null && (
        <div className="bg-white/[0.02] border border-white/[0.10] p-4">
          <p className="text-[10px] tracking-[0.2em] uppercase text-white/60 mb-2">STR Break-even</p>
          <p className="text-white text-sm">
            You need <span className="text-[#D4AF37]">{result.breakEvenOccupancyPct.toFixed(0)}%</span> occupancy to break even
          </p>
          <p className="text-white/60 text-xs mt-1">
            Currently modelling {draft.occupancy_pct}% · {fmt(result.occupiedNightsPerYear)} nights / year
          </p>
        </div>
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="bg-amber-500/[0.04] border border-amber-500/20 p-3 space-y-2" data-testid="deal-warnings">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
            <p className="text-amber-200/90 text-[10px] tracking-wider uppercase">Sanity Check ({warnings.length})</p>
          </div>
          {warnings.map((w, i) => (
            <p key={i} className="text-amber-100/70 text-xs leading-relaxed pl-5">· {w}</p>
          ))}
        </div>
      )}

      {/* Convert to Asset */}
      <button
        type="button"
        onClick={onConvert}
        disabled={converting || isConverted || result.totalProjectCost <= 0}
        data-testid="convert-to-asset-btn"
        className="w-full bg-[#D4AF37] hover:bg-[#E8C757] disabled:bg-white/10 disabled:text-white/65 disabled:cursor-not-allowed text-black text-xs font-semibold tracking-[0.15em] uppercase px-5 py-3 inline-flex items-center justify-center gap-2 transition-colors"
      >
        <Sparkles className="w-4 h-4" />
        {isConverted ? "Already converted" : converting ? "Converting…" : "Convert to Asset"}
      </button>
      {!isConverted && (
        <p className="text-white/65 text-[10px] text-center leading-relaxed">
          Migrates this draft into your live portfolio. Use when you've actually bought the property.
        </p>
      )}
    </div>
  );
}

function MetricCard({ label, value, subtitle, testId, highlight, tip }) {
  return (
    <div className={`p-3 border ${highlight ? "border-[#D4AF37]/20 bg-[#D4AF37]/[0.02]" : "border-white/[0.10] bg-white/[0.01]"}`} data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-1">
        <p className="text-[9px] tracking-wider uppercase text-white/55">{label}</p>
        {tip && <InfoTip text={tip} />}
      </div>
      <p className="text-white text-xl tabular-nums font-serif">{value}</p>
      <p className="text-white/65 text-[10px] mt-0.5">{subtitle}</p>
    </div>
  );
}

function Row({ label, value, muted, bold, faint, highlight }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className={`${faint ? "text-white/65 text-[11px]" : muted ? "text-white/60" : "text-white"} ${bold ? "font-medium" : ""}`}>{label}</span>
      <span className={`tabular-nums ${faint ? "text-white/60 text-[11px]" : highlight ? "text-emerald-400" : bold ? "text-white font-medium" : "text-white/90"}`}>{value}</span>
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="border border-white/[0.10] py-16 px-6 text-center max-w-md mx-auto">
      <Calculator className="w-10 h-10 text-[#D4AF37]/40 mx-auto mb-4" strokeWidth={1.2} />
      <h3 className="text-white font-serif text-xl mb-2">No deals yet</h3>
      <p className="text-white/60 text-sm mb-6 leading-relaxed">
        Run the numbers on any property you're considering — Dubai, North Cyprus, Germany. Once you buy it, convert it to a tracked asset.
      </p>
      <button
        type="button"
        onClick={onCreate}
        className="bg-[#D4AF37] hover:bg-[#E8C757] text-black text-xs font-semibold tracking-[0.15em] uppercase px-5 py-2.5 inline-flex items-center gap-2 transition-colors"
      >
        <Plus className="w-4 h-4" /> Start your first deal
      </button>
    </div>
  );
}
